import { memo, useCallback, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

import { useFocusEffect } from '@react-navigation/native';

import { InfoIconCircle } from '@/assets/icons/info-circle';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { useGlobalState } from '@/hooks/use-global-state';
import { RaceToPlaceEntry } from '@/types/socialFeed.generated';

import { RaceToPlaceSkeleton } from '../Skeletons/RaceToPlaceSkeleton';
import { RaceToPlaceLobby } from './RaceToPlaceLobby';
import { StackedCards } from './StackedCards';
import { raceToPlaceService } from './api/RaceToPlaceService';
import { useRaceToPlaceInfoSheet } from './hooks/use-race-to-place-info-sheet';
import { useRaceToPlaceStore } from './hooks/use-race-to-place-store';
import { RACE_TO_PLACE_LOBBY_HEIGHT, RACE_TO_PLACE_LOBBY_MARGIN_TOP } from './race-to-place-constants';

export const RaceToPlaceLobbySection = memo(({ title }: { title: string }) => {
    const raceToPlaceData = useRaceToPlaceStore(state => state.raceToPlaceData);
    const loading = useRaceToPlaceStore(state => state.loading);
    const error = useRaceToPlaceStore(state => state.error);
    const raceToPlaceEnabled = useGlobalState(state => state.entryFeedConfig?.raceToPlaceEnabled);
    const showRaceToPlaceInfoSheet = useRaceToPlaceInfoSheet();

    useFocusEffect(
        useCallback(() => {
            if (!raceToPlaceEnabled) {
                return;
            }
            raceToPlaceService.connect();
            return () => {
                raceToPlaceService.disconnect();
            };
        }, [raceToPlaceEnabled])
    );

    const showSkeleton = useMemo(() => loading && !raceToPlaceData, [loading, raceToPlaceData]);

    const renderCard = useCallback(
        (entry: RaceToPlaceEntry, cardHeight: SharedValue<number>) => (
            <RaceToPlaceLobby raceToPlaceEntry={entry} cardHeight={cardHeight} />
        ),
        []
    );

    const renderSkeleton = useCallback(() => <RaceToPlaceSkeleton />, []);

    if ((!raceToPlaceData && (!loading || error)) || !raceToPlaceEnabled) {
        return null;
    }

    return (
        <Box style={styles.containerBorder}>
            <Box style={styles.container}>
                <Row alignItems={'center'}>
                    <Text color={ds.colorToken.text.primary} variant={'headlineMedium'} lineHeight={28}>
                        {title}
                    </Text>
                    <TouchableOpacity style={styles.infoContainer} onPress={showRaceToPlaceInfoSheet}>
                        <InfoIconCircle />
                    </TouchableOpacity>
                </Row>
                <StackedCards
                    containerStyle={styles.stackedCards}
                    data={raceToPlaceData}
                    placeholderHeight={RACE_TO_PLACE_LOBBY_HEIGHT}
                    renderCard={renderCard}
                    renderSkeleton={showSkeleton ? renderSkeleton : undefined}
                />
            </Box>
        </Box>
    );
});

const styles = StyleSheet.create({
    containerBorder: {
        borderTopWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    infoContainer: {
        paddingHorizontal: 8,
    },
    container: {
        paddingTop: 16,
        paddingBottom: 24,
        paddingHorizontal: 16,
    },
    stackedCards: {
        marginTop: RACE_TO_PLACE_LOBBY_MARGIN_TOP,
    },
});
